/* -------------------------------------------------------------------------
 * TJR Load More — AJAX pagination for the collection page.
 *
 * Self-contained. Pairs with sections/tjr-load-more.liquid.
 *
 * Behaviour:
 *   - Finds the theme's existing <product-list id="product-list-…"> grid and
 *     derives the main-collection section id from it.
 *   - "Load More" fetches the next page via the Section Rendering API
 *     (?section_id=…&page=N, preserving any active filter/sort params) and
 *     appends the new product cards into the live grid.
 *   - Reflects the loaded page in the URL (?page=N) so a refresh reproduces the
 *     same view: on load, if ?page=N (N>1) the earlier pages (1..N-1) are
 *     re-fetched and prepended (the theme only renders page N server-side).
 *   - Re-syncs with the theme's own facet system: when a filter/sort is applied,
 *     the theme replaces the collection section's content via `facet:update`
 *     (theme.js). A MutationObserver on the section wrapper detects that swap
 *     and re-reads the filtered product count / page state so the count, the
 *     "Page N" label and the button visibility stay correct.
 *   - Updates the "1 – X of Y Products" count and the "Page N" label.
 *   - Hides the button when the last page is reached (or there are no results).
 *
 * Touches no existing files; remove the section to fully revert.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var GRID_SELECTOR = '[id^="product-list-"]';
  var COUNT_SELECTOR = '.shopify-section--main-collection .collection-toolbar__products-count';

  function init() {
    var root = document.querySelector('[data-tjr-loadmore]');
    if (!root) return;

    var btn = root.querySelector('[data-tjr-loadmore-btn]');
    var countEl = root.querySelector('[data-tjr-loadmore-count]');
    var pageEl = root.querySelector('[data-tjr-loadmore-page]');
    if (!btn || !countEl || !pageEl) return;

    var perPage = parseInt(root.getAttribute('data-per-page'), 10) || 48;
    var productsWord = root.getAttribute('data-products-word') || 'Products';
    var pageWord = root.getAttribute('data-page-word') || 'Page';

    // Locate the live product grid rendered by the theme's main-collection section.
    var list = document.querySelector(GRID_SELECTOR);
    if (!list) {
      root.style.display = 'none';
      return;
    }

    var sectionWrap = list.closest('.shopify-section');
    var sectionId = sectionWrap ? sectionWrap.id.replace('shopify-section-', '') : null;

    // Mutable state — re-derived on every filter/sort re-render.
    var total = parseInt(root.getAttribute('data-total'), 10) || 0;
    var totalPages = 1;
    var currentPage = 1;
    var loading = false;

    /* --------------------------------------------------------------------- */
    /* Helpers                                                               */
    /* --------------------------------------------------------------------- */

    // Read the current (possibly filtered) total from the theme's results
    // count, falling back to the highest numbered pagination link, then to
    // whatever we already had.
    function readTotal() {
      var countNode = document.querySelector(COUNT_SELECTOR);
      if (countNode) {
        var digits = countNode.textContent.replace(/[^\d]/g, '');
        if (digits !== '') return parseInt(digits, 10);
      }
      // Fallback: derive from the (hidden) pagination nav's last numbered link.
      var pages = 0;
      document
        .querySelectorAll('.shopify-section--main-collection nav.pagination a, .shopify-section--main-collection nav.pagination span')
        .forEach(function (el) {
          var n = parseInt(el.textContent.trim(), 10);
          if (!isNaN(n) && n > pages) pages = n;
        });
      if (pages > 0) return pages * perPage; // approximate, only used for button visibility
      return total;
    }

    function readCurrentPage() {
      var p = parseInt(new URLSearchParams(window.location.search).get('page'), 10) || 1;
      if (p < 1) p = 1;
      if (p > totalPages) p = totalPages;
      return p;
    }

    function paint() {
      var hasResults = total > 0 && !!list;
      var end = Math.min(currentPage * perPage, total);
      var start = hasResults ? 1 : 0;
      countEl.textContent = start + ' – ' + (hasResults ? end : 0) + ' of ' + total + ' ' + productsWord;
      pageEl.textContent = pageWord + ' ' + currentPage;
      btn.hidden = !hasResults || currentPage >= totalPages;
    }

    // Build a URL for a given page.
    //   forFetch=true  -> includes section_id (Section Rendering API request)
    //   forFetch=false -> clean URL for the address bar (no section_id)
    function buildUrl(page, forFetch) {
      var url = new URL(window.location.href);
      if (page > 1) {
        url.searchParams.set('page', String(page));
      } else {
        url.searchParams.delete('page');
      }
      if (forFetch && sectionId) {
        url.searchParams.set('section_id', sectionId);
      } else {
        url.searchParams.delete('section_id');
      }
      return url;
    }

    function fetchPageHtml(page) {
      return fetch(buildUrl(page, true).toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      }).then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      });
    }

    function parseChildren(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var newList = doc.querySelector(GRID_SELECTOR);
      if (!newList) return [];
      return Array.prototype.slice.call(newList.children).map(function (node) {
        // adoptNode moves the node into the current document so custom
        // elements (product-card, quick-add, etc.) upgrade on insertion.
        return document.adoptNode(node);
      });
    }

    function updateHistory(page) {
      try {
        window.history.replaceState(window.history.state, '', buildUrl(page, false).toString());
      } catch (e) { /* replaceState can throw in rare sandboxed contexts */ }
    }

    function setBusy(state) {
      loading = state;
      btn.classList.toggle('is-loading', state);
      if (state) {
        btn.setAttribute('aria-busy', 'true');
      } else {
        btn.removeAttribute('aria-busy');
      }
    }

    // Recompute all derived state from the current DOM/URL and repaint.
    // Called on init and after every theme filter/sort re-render.
    function recomputeState() {
      list = document.querySelector(GRID_SELECTOR); // theme may have swapped this node
      total = readTotal();
      totalPages = perPage > 0 && total > 0 ? Math.ceil(total / perPage) : 1;
      currentPage = readCurrentPage();
      paint();
    }

    /* --------------------------------------------------------------------- */
    /* Load More                                                             */
    /* --------------------------------------------------------------------- */

    btn.addEventListener('click', function () {
      if (loading || !list || currentPage >= totalPages) return;
      setBusy(true);

      var nextPage = currentPage + 1;

      // Anchor scroll to the last existing product so the browser's scroll
      // anchoring doesn't fling the user to the end when cards are inserted.
      var anchorEl = list.lastElementChild;
      var anchorTop = anchorEl ? anchorEl.getBoundingClientRect().top : null;

      fetchPageHtml(nextPage)
        .then(function (html) {
          var frag = document.createDocumentFragment();
          parseChildren(html).forEach(function (node) { frag.appendChild(node); });
          list.appendChild(frag);

          currentPage = nextPage;
          paint();
          updateHistory(currentPage);

          if (anchorEl && anchorTop !== null) {
            requestAnimationFrame(function () {
              var delta = anchorEl.getBoundingClientRect().top - anchorTop;
              if (delta) window.scrollBy(0, delta);
            });
          }
        })
        .catch(function (err) {
          // eslint-disable-next-line no-console
          console.error('[tjr-load-more]', err);
        })
        .then(function () { setBusy(false); });
    });

    /* --------------------------------------------------------------------- */
    /* Deep-link reconstruction (?page=N on first load)                      */
    /* --------------------------------------------------------------------- */

    function reconstructEarlierPages() {
      var startPage = readCurrentPage();
      var pages = [];
      for (var p = 1; p < startPage; p++) pages.push(p);
      if (!pages.length) return;

      setBusy(true);
      Promise.all(pages.map(fetchPageHtml))
        .then(function (htmls) {
          var frag = document.createDocumentFragment();
          htmls.forEach(function (html) {
            parseChildren(html).forEach(function (node) { frag.appendChild(node); });
          });
          list.insertBefore(frag, list.firstChild); // pages 1..N-1 before server-rendered page N
          currentPage = startPage;
          paint();
        })
        .catch(function (err) {
          // eslint-disable-next-line no-console
          console.error('[tjr-load-more]', err);
        })
        .then(function () { setBusy(false); });
    }

    /* --------------------------------------------------------------------- */
    /* Re-sync with the theme's facet (filter/sort) system                   */
    /* --------------------------------------------------------------------- */

    // The theme replaces the *direct children* of the section wrapper on
    // `facet:update` (theme.js). Observing childList WITHOUT subtree means our
    // own deep appends into <product-list> never trigger this — only the
    // theme's re-render does.
    if (sectionWrap && 'MutationObserver' in window) {
      var resyncTimer = null;
      var observer = new MutationObserver(function () {
        if (resyncTimer) clearTimeout(resyncTimer);
        resyncTimer = setTimeout(function () {
          // A filter/sort reset the grid to page 1; reflect that state.
          recomputeState();
        }, 0);
      });
      observer.observe(sectionWrap, { childList: true, subtree: false });
    }

    /* --------------------------------------------------------------------- */
    /* Boot                                                                  */
    /* --------------------------------------------------------------------- */

    recomputeState();
    reconstructEarlierPages();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
